import { cn } from "@-/com.npmjs.cn";
import {
  WebsiteContainer,
  websiteContainerVariants,
  WebsiteEyebrow,
  WebsiteHeading,
  WebsiteSection,
  WebsiteText,
} from "@-/dev.improve.haniverse.components.website";
import { LandingDeviceFrame } from "#landing-device-frames";
import { LANDING_SCREENSHOTS, type LandingScreenshot } from "#landing-screenshots";

const LANDING_TOUR_STOPS: { caption: string; screenshot: LandingScreenshot; title: string }[] = [
  {
    caption: "Watch each new character write itself, stroke by stroke.",
    screenshot: LANDING_SCREENSHOTS.iosLesson,
    title: "Meet a character",
  },
  {
    caption: "Its parts, their sounds and a story that ties them together.",
    screenshot: LANDING_SCREENSHOTS.androidMnemonic,
    title: "See why it looks that way",
  },
  {
    caption: "Step through the stroke order on a practice grid.",
    screenshot: LANDING_SCREENSHOTS.iosStrokes,
    title: "Write it properly",
  },
  {
    caption: "Reviews come back right before you’d forget, scheduled by FSRS.",
    screenshot: LANDING_SCREENSHOTS.androidReview,
    title: "Remember it for good",
  },
  {
    caption: "Work through levels where every new character is made of pieces you know.",
    screenshot: LANDING_SCREENSHOTS.iosBrowse,
    title: "Follow a real curriculum",
  },
];

/** A scrollable strip of real app screens after the promises: "what will I actually get?" */
export function LandingScreenTour() {
  return (
    <WebsiteSection id="tour">
      <WebsiteContainer className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <WebsiteEyebrow tone="muted">Inside the app</WebsiteEyebrow>
          <WebsiteHeading className="mt-4 max-w-[16ch]" level={2}>
            From first stroke to lasting recall.
          </WebsiteHeading>
        </div>
        <WebsiteText className="max-w-[24rem]">
          Every screen below is the real app, captured on iPhone and Android.
        </WebsiteText>
      </WebsiteContainer>

      {/* The strip is the content column itself, so phones can scroll it edge to edge. */}
      <ol
        className={cn(
          websiteContainerVariants(),
          "mt-12 mb-0 flex snap-x snap-mandatory scrollbar-none list-none gap-5 overflow-x-auto pb-6 lg:grid lg:grid-cols-5 lg:gap-6 lg:overflow-visible [&::-webkit-scrollbar]:hidden",
        )}
      >
        {LANDING_TOUR_STOPS.map((stop, index) => (
          <li
            className="w-[62vw] max-w-[15rem] shrink-0 snap-center lg:w-auto lg:max-w-none"
            key={stop.title}
          >
            <LandingDeviceFrame className="w-full" screenshot={stop.screenshot} />
            <WebsiteEyebrow className="mt-6">{String(index + 1).padStart(2, "0")}</WebsiteEyebrow>
            <WebsiteHeading className="mt-1.5" level={3} size="label">
              {stop.title}
            </WebsiteHeading>
            <WebsiteText className="mt-1.5" size="caption">
              {stop.caption}
            </WebsiteText>
          </li>
        ))}
      </ol>
    </WebsiteSection>
  );
}
